import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, withOrg } from '../config';
import { notify, notifyError } from '../notify';
import { th, td, useSort, sortRows, HeaderCell } from '../components/sortTable';
import { Chip, RuleHints } from '../components/subBits';

// Competitors: paste Reddit profiles to get inspired by: where, how often and
// when they post (read once, and again whenever someone clicks Re-read), and
// the subs they post in. A sub enters our database only when someone adds it.

const SCRAPE_USD_PER_SUB = 0.022; // the scraper's measured Claude cost per sub (Sep 2026)
const STATUS = {
  queued: { label: 'Waiting', color: '#888888' },
  ok: { label: 'Read', color: '#22c55e' },
  not_found: { label: 'Not found', color: '#ef4444' },
  suspended: { label: 'Suspended', color: '#ef4444' }
};
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MEDIA_LABEL = { image: 'images', gif: 'GIFs', gallery: 'galleries', video: 'videos', redgifs: 'RedGIFs', link: 'links', text: 'text' };

const tabStyle = (active) => ({
  padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', borderRadius: '8px 8px 0 0',
  background: active ? '#1a1a1a' : 'transparent', color: active ? '#ff4500' : '#666',
  borderBottom: active ? '2px solid #ff4500' : '2px solid transparent'
});
const pillStyle = (active) => ({
  padding: '6px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  background: active ? '#ff4500' : '#2a2a2a', color: active ? '#ffffff' : '#aaaaaa'
});
const link = { color: '#ff4500', textDecoration: 'none' };

function age(iso) {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days < 1 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
}
const num = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
const time = (iso) => (iso ? Date.parse(iso) : null);
const karmaOf = (p) => p.total_karma ?? (p.post_karma != null ? p.post_karma + (p.comment_karma || 0) : null);
// Being read, or asked to be (a read is due now); a later date is not a request.
const readPending = (p) => p.reading || (!!p.next_read_at && Date.parse(p.next_read_at) <= Date.now() + 60000);

function SubFlags({ sub }) {
  return <>
    {sub.over_18 && <Chip color="#ef4444">NSFW</Chip>}
    {sub.subreddit_type === 'restricted' && <Chip color="#a78bfa" title="Only approved users can post; everyone else gets “Request to Post”">✋ Request to Post</Chip>}
  </>;
}

const unusable = (s) => !s.db && (s.karma_farm || (s.check && ['gone', 'karma_farm', 'off_limits'].includes(s.check.status)));

// Where the sub stands for us: new, dismissed, unusable, or in our database (and how far).
function ReviewState({ sub }) {
  const db = sub.db;
  if (!db) {
    if (sub.karma_farm || (sub.check && sub.check.status === 'karma_farm')) return <span style={{ color: '#ef4444' }}>Karma farm · never</span>;
    if (sub.check && sub.check.status === 'off_limits') return <span style={{ color: '#ef4444' }} title="A SFW sub for teenagers, kids, school or support: never a place for a creator's account">Off limits · never</span>;
    if (sub.check && sub.check.status === 'gone') return <span style={{ color: '#666' }}>Gone (closed or banned)</span>;
    if (sub.dismissed) return <span style={{ color: '#666' }}>Dismissed</span>;
    return <span style={{ color: '#f59e0b' }}>New{sub.check && sub.check.status === 'queued' ? ' · checking size…' : ''}</span>;
  }
  if (db.is_dead) return <span style={{ color: '#ef4444' }}>In database · dead</span>;
  if (db.scrape_status === 'discovered') return <span style={{ color: '#60a5fa' }}>Added · not scraped</span>;
  if (db.scrape_status === 'pending' || db.scrape_status === 'scraping') return <span style={{ color: '#60a5fa' }}>Scraping…</span>;
  return <span style={{ color: '#22c55e' }}>In database</span>;
}

const subCell = { key: 'name', label: 'Subreddit', value: s => s.subreddit.toLowerCase(), render: s => <><a href={`https://www.reddit.com/r/${s.subreddit}/`} target="_blank" rel="noreferrer" style={link}>r/{s.subreddit}</a><SubFlags sub={s} /></> };
const sizeCell = { key: 'size', label: 'Weekly visitors', value: s => s.weekly_visitors, render: s => num(s.weekly_visitors) };

const WHERE_COLUMNS = [
  subCell, sizeCell,
  { key: 'posts', label: 'Posts', value: s => s.posts, render: s => s.posts },
  { key: 'share', label: 'Share', value: s => s.share_pct, render: s => `${s.share_pct}%` },
  { key: 'per_week', label: 'Per week', value: s => s.per_week, render: s => s.per_week },
  { key: 'gap', label: 'Typical gap', value: s => s.typical_gap_days, render: s => (s.typical_gap_days === null ? '—' : `${s.typical_gap_days}d`) },
  { key: 'last', label: 'Last post', value: s => time(s.last_post), render: s => age(s.last_post) },
  { key: 'median', label: 'Median ▲', value: s => s.median_score, render: s => num(s.median_score) },
  { key: 'best', label: 'Best ▲', value: s => s.best_score, render: s => num(s.best_score) },
  { key: 'comments', label: 'Comments', value: s => s.avg_comments, render: s => s.avg_comments },
  { key: 'removed', label: 'Removed', value: s => s.removed, render: s => <span style={{ color: s.removed ? '#ef4444' : '#555' }}>{s.removed || '—'}</span> },
  { key: 'state', label: 'For us', render: s => <ReviewState sub={s} /> }
];

const LIST_COLUMNS = [
  subCell, sizeCell,
  { key: 'profiles', label: 'Profiles', value: s => s.profiles.length, render: s => <span title={s.profiles.map(u => 'u/' + u).join(', ')}>{s.profiles.length}</span> },
  { key: 'posts', label: 'Posts', value: s => s.posts, render: s => s.posts },
  { key: 'per_week', label: 'Per week', value: s => s.per_week, render: s => s.per_week },
  { key: 'median', label: 'Median ▲', value: s => s.median_score, render: s => num(s.median_score) },
  { key: 'best', label: 'Best ▲', value: s => s.best_score, render: s => num(s.best_score) },
  { key: 'last', label: 'Last post', value: s => time(s.last_post), render: s => age(s.last_post) },
  { key: 'rules', label: 'Rules mention', render: s => <RuleHints check={s.check} /> },
  { key: 'state', label: 'Status', render: s => <ReviewState sub={s} /> }
];

function SubTable({ rows, columns, sort, onSort, selectable }) {
  const { selected, toggle, toggleAll } = selectable || {};
  const allPicked = !!selectable && rows.length > 0 && rows.every(s => selected.has(s.sub_key));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead><tr>
          {selectable && <th style={th}><input type="checkbox" checked={allPicked} onChange={() => toggleAll(allPicked)} style={{ flex: 'none' }} /></th>}
          {columns.map(c => <HeaderCell key={c.key} col={c} sort={sort} onSort={onSort} />)}
        </tr></thead>
        <tbody>
          {sortRows(rows, sort, columns).map(s => (
            <tr key={s.sub_key}>
              {selectable && <td style={td}><input type="checkbox" checked={selected.has(s.sub_key)} onChange={() => toggle(s.sub_key)} style={{ flex: 'none' }} /></td>}
              {columns.map(c => <td key={c.key} style={td}>{c.render(s)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddProfiles({ onAdded, orgId }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/competitors/profiles`, { input: text }, withOrg(orgId));
      const bits = [];
      if (data.added.length) bits.push(`${data.added.length} added`);
      if (data.already.length) bits.push(`${data.already.length} already here`);
      if (data.invalid.length) bits.push(`not recognised: ${data.invalid.join(', ')}`);
      notify(bits.join(' · '), data.invalid.length ? 'info' : 'success', data.invalid.length ? 8000 : 3000);
      setText(data.invalid.join('\n')); // what's left to fix
      onAdded();
    } catch (err) { notifyError(err, 'Could not add the profiles'); }
    setBusy(false);
  };
  return (
    <div className="card">
      <h2>Add profiles</h2>
      <textarea rows={4} value={text} onChange={e => setText(e.target.value)} style={{ width: '100%' }}
        placeholder={'Usernames or profile links, one per line or separated by commas\nu/someone, https://www.reddit.com/user/someone_else/'} />
      <div className="form-row" style={{ marginTop: '10px', justifyContent: 'space-between' }}>
        <span className="sub">The scraper reads each profile's newest posts (up to 1,000) within minutes. Re-read it whenever you want fresh numbers. No AI cost.</span>
        <button onClick={add} disabled={busy || !text.trim()}>{busy ? 'Adding…' : 'Add'}</button>
      </div>
    </div>
  );
}

function ProfileRow({ p, open, onOpen, onReread, onRemove }) {
  const s = STATUS[p.status] || { label: p.status, color: '#888' };
  const sum = p.summary;
  const waiting = readPending(p);
  return (
    <div className="list-item" style={{ borderColor: open ? '#ff4500' : undefined }}>
      <div className="list-item-main">
        <strong>
          <a href={`https://www.reddit.com/user/${p.reddit_username}/`} target="_blank" rel="noreferrer" style={link}>u/{p.reddit_username}</a>
          <span className="status-badge" style={{ background: p.reading ? '#f59e0b' : s.color }}>{p.reading ? 'Reading' : s.label}</span>
        </strong>
        <span className="sub">
          {karmaOf(p) != null ? `${num(karmaOf(p))} karma · ` : ''}
          {sum.posts ? `${num(sum.posts)} posts over ${sum.days} days · ${sum.posts_per_day}/day · ${sum.subs} subs` : 'No posts read yet'}
          {p.read_at ? ` · read ${age(p.read_at)}` : ''}
        </span>
        {p.last_error && <span className="sub" style={{ color: '#ef4444' }}>{p.last_error}</span>}
      </div>
      <div className="list-item-actions">
        <button className="btn-secondary btn-small" onClick={() => onOpen(open ? null : p.id)} disabled={!sum.posts}>{open ? 'Close' : 'Open'}</button>
        <button className="btn-secondary btn-small" onClick={() => onReread(p)} disabled={!!waiting}>{waiting ? 'Queued' : 'Re-read'}</button>
        <button className="btn-danger btn-small" onClick={() => onRemove(p)}>Remove</button>
      </div>
    </div>
  );
}

function StatBox({ value, label }) {
  return <div className="stat-box"><div className="stat-box-value">{value}</div><div className="stat-box-label">{label}</div></div>;
}

// Weekday × hour (UTC), shaded by how many posts went out then.
function WhenGrid({ when }) {
  const cells = new Map(when.grid.map(c => [`${c.day}-${c.hour}`, c]));
  const max = Math.max(1, ...when.grid.map(c => c.posts));
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '36px repeat(24, minmax(18px, 1fr))', gap: '2px', fontSize: '10px', color: '#666', minWidth: '520px' }}>
        <span />
        {Array.from({ length: 24 }, (_, h) => <span key={h} style={{ textAlign: 'center' }}>{h % 3 === 0 ? h : ''}</span>)}
        {DAYS.map((d, day) => (
          <React.Fragment key={d}>
            <span>{d}</span>
            {Array.from({ length: 24 }, (_, hour) => {
              const c = cells.get(`${day}-${hour}`);
              return <span key={hour} title={c ? `${d} ${hour}:00 UTC — ${c.posts} posts, avg ▲${c.avg_upvotes}` : `${d} ${hour}:00 UTC — no posts`}
                style={{ height: '18px', borderRadius: '3px', background: c ? `rgba(255, 69, 0, ${0.15 + 0.85 * (c.posts / max)})` : '#161616' }} />;
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function PerDayBars({ days }) {
  const shown = days.slice(-90);
  const max = Math.max(1, ...shown.map(d => d.posts));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: '70px' }}>
      {shown.map(d => <div key={d.date} title={`${d.date}: ${d.posts} posts`} style={{ flex: 1, height: `${Math.max(2, (d.posts / max) * 100)}%`, background: d.posts ? '#ff4500' : '#222', borderRadius: '2px 2px 0 0' }} />)}
    </div>
  );
}

function ProfileDetail({ id, onClose, orgId }) {
  const [data, setData] = useState(null);
  const [sort, onSort] = useSort({ key: 'posts', dir: 'desc' });
  useEffect(() => {
    axios.get(`${API}/api/competitors/profiles/${id}`, withOrg(orgId)).then(r => setData(r.data)).catch(err => notifyError(err, 'Could not load the profile'));
  }, [id, orgId]);
  if (!data) return <div className="card"><p className="empty">Loading…</p></div>;
  const { profile, summary, subs, when, per_day: perDay, sittings } = data;
  const mediaMix = Object.entries(summary.media).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${Math.round((n / summary.posts) * 100)}% ${MEDIA_LABEL[k] || k}`).join(' · ');
  return (
    <div className="card">
      <div className="form-row" style={{ justifyContent: 'space-between', marginBottom: '12px' }}>
        <h2 style={{ margin: 0 }}>u/{profile.reddit_username}</h2>
        <button className="btn-secondary btn-small" onClick={onClose}>Close</button>
      </div>
      <p className="sub" style={{ marginBottom: '12px' }}>
        {num(summary.posts)} posts from the last {summary.days} days
        {profile.account_created_at ? ` · account ${age(profile.account_created_at).replace(' ago', '')} old` : ''}
        {summary.profile_posts ? ` · ${summary.profile_posts} on the profile itself` : ''}
        {summary.crossposts ? ` · ${summary.crossposts} crossposts` : ''}
        {summary.removed ? ` · ${summary.removed} marked removed` : ''}
      </p>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <StatBox value={num(karmaOf(profile))} label={profile.total_karma != null ? 'Total karma' : 'Post + comment karma'} />
        <StatBox value={summary.posts_per_day} label="Posts per day" />
        <StatBox value={summary.active_days_per_week} label="Active days / week" />
        <StatBox value={summary.subs} label="Subreddits" />
        <StatBox value={sittings.per_active_day ?? '—'} label="Sittings / active day" />
        <StatBox value={sittings.median_posts ?? '—'} label={`Posts per sitting (~${sittings.median_minutes ?? 0} min)`} />
      </div>
      <p className="sub" style={{ marginBottom: '16px' }}>
        Content: {mediaMix || '—'}
        {profile.post_karma != null ? ` · karma: ${num(profile.post_karma)} post, ${num(profile.comment_karma)} comment` : ''}
      </p>

      <h2>When (UTC)</h2>
      <WhenGrid when={when} />
      <p className="sub" style={{ margin: '12px 0 6px' }}>Posts per day{perDay.length > 90 ? ' (last 90 days)' : ''}</p>
      <PerDayBars days={perDay} />

      <h2 style={{ marginTop: '20px' }}>Where ({subs.length} subreddits)</h2>
      <SubTable rows={subs} columns={WHERE_COLUMNS} sort={sort} onSort={onSort} />
    </div>
  );
}

const PROFILE_SORTS = [
  { key: 'name', label: 'Name', value: p => p.reddit_username.toLowerCase(), dir: 'asc' },
  { key: 'karma', label: 'Karma', value: karmaOf },
  { key: 'per_day', label: 'Posts per day', value: p => p.summary.posts_per_day },
  { key: 'subs', label: 'Subreddits', value: p => p.summary.subs },
  { key: 'read', label: 'Last read', value: p => time(p.read_at) }
];

function ProfilesTab({ orgId }) {
  const [profiles, setProfiles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const load = useCallback(() => axios.get(`${API}/api/competitors/profiles`, withOrg(orgId))
    .then(r => { setProfiles(r.data); setLoaded(true); })
    .catch(err => { notifyError(err, 'Could not load the profiles'); setLoaded(true); }), [orgId]);
  useEffect(() => { load(); }, [load]);
  const waiting = profiles.some(readPending);
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [waiting, load]);
  const reread = async (path, done) => {
    try {
      const { data } = await axios.post(`${API}/api/competitors/profiles/${path}`, null, withOrg(orgId));
      notify(done(data.queued), 'success');
      load();
    } catch (err) { notifyError(err, 'Could not queue the re-read'); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Stop analysing u/${p.reddit_username}?`)) return;
    try {
      await axios.delete(`${API}/api/competitors/profiles/${p.id}`, withOrg(orgId));
      if (openId === p.id) setOpenId(null);
      load();
    } catch (err) { notifyError(err, 'Could not remove the profile'); }
  };
  const sortBy = PROFILE_SORTS.find(s => s.key === sortKey);
  const shown = sortRows(profiles, { key: sortKey, dir: sortBy.dir || 'desc' }, PROFILE_SORTS);
  return <>
    <AddProfiles onAdded={load} orgId={orgId} />
    <div className="card">
      <div className="form-row" style={{ justifyContent: 'space-between', marginBottom: '12px' }}>
        <h2 style={{ margin: 0 }}>Profiles</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span className="sub">Sort by</span>
          <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={{ flex: 'none', padding: '6px 10px' }}>
            {PROFILE_SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button className="btn-secondary btn-small" disabled={!profiles.length} onClick={() => reread('reread', n => `${n} profile(s) queued for a re-read`)}>Re-read all</button>
        </div>
      </div>
      {!loaded ? <p className="empty">Loading…</p>
        : profiles.length === 0 ? <p className="empty">No profiles yet. Add some above.</p>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {shown.map(p => <ProfileRow key={p.id} p={p} open={openId === p.id} onOpen={setOpenId} onRemove={remove}
              onReread={x => reread(`${x.id}/reread`, () => `u/${x.reddit_username} queued for a re-read`)} />)}
          </div>}
    </div>
    {openId && <ProfileDetail key={openId} id={openId} orgId={orgId} onClose={() => setOpenId(null)} />}
  </>;
}

const FILTERS = [
  { id: 'new', label: 'New', test: s => !s.db && !s.dismissed && !unusable(s) },
  { id: 'db', label: 'In database', test: s => !!s.db },
  { id: 'dismissed', label: 'Dismissed', test: s => !s.db && s.dismissed && !unusable(s) },
  { id: 'unusable', label: 'Gone / karma farm', test: unusable }
];

function SubListTab({ orgId }) {
  const [subs, setSubs] = useState(null);
  const [filter, setFilter] = useState('new');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [sort, onSort] = useSort({ key: 'profiles', dir: 'desc' });
  const load = useCallback(() => axios.get(`${API}/api/competitors/subs`, withOrg(orgId))
    .then(r => setSubs(r.data)).catch(err => notifyError(err, 'Could not load the sub list')), [orgId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [filter]);
  if (!subs) return <div className="card"><p className="empty">Loading…</p></div>;

  const shown = subs.filter(FILTERS.find(f => f.id === filter).test);
  const picked = shown.filter(s => selected.has(s.sub_key));
  const selectable = {
    selected,
    toggle: (key) => setSelected(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; }),
    toggleAll: (allPicked) => setSelected(allPicked ? new Set() : new Set(shown.map(s => s.sub_key)))
  };
  const act = async (path, body, done) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/competitors/subs/${path}`, { subs: picked.map(s => s.subreddit), ...body }, withOrg(orgId));
      notify(done(data), 'success');
      setSelected(new Set());
      await load();
    } catch (err) { notifyError(err, 'Could not update the subs'); }
    setBusy(false);
  };
  const toScrape = picked.filter(s => !s.db || s.db.scrape_status === 'discovered');
  const addAndScrape = () => {
    const cost = (toScrape.length * SCRAPE_USD_PER_SUB).toFixed(2);
    if (window.confirm(`Scrape ${toScrape.length} sub(s)? About $${cost} in AI classification.`)) {
      act('add', { scrape: true }, d => `${d.added} added, ${d.queued} queued for a scrape`);
    }
  };

  return (
    <div className="card">
      <div className="form-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {FILTERS.map(f => <button key={f.id} style={pillStyle(filter === f.id)} onClick={() => setFilter(f.id)}>{f.label} ({subs.filter(f.test).length})</button>)}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {filter === 'new' && <button className="btn-secondary btn-small" disabled={busy || !picked.length} onClick={() => act('add', { scrape: false }, d => `${d.added} added (not scraped)`)}>Add{picked.length ? ` ${picked.length}` : ''}</button>}
          {(filter === 'new' || filter === 'db') && <button className="btn-small" disabled={busy || !toScrape.length} onClick={addAndScrape}>{filter === 'new' ? 'Add & scrape' : 'Scrape'}{toScrape.length ? ` ${toScrape.length}` : ''}</button>}
          {filter === 'new' && <button className="btn-danger btn-small" disabled={busy || !picked.length} onClick={() => act('dismiss', {}, d => `${d.dismissed} dismissed`)}>Dismiss</button>}
          {filter === 'dismissed' && <button className="btn-secondary btn-small" disabled={busy || !picked.length} onClick={() => act('restore', {}, d => `${d.restored} back in New`)}>Restore</button>}
        </div>
      </div>
      <p className="sub" style={{ marginBottom: '10px' }}>Every subreddit the profiles post in. Click a column title to sort. Open one on Reddit to check it, then add it (stays unscraped until you ask), add and scrape it, or dismiss it. Weekly visitors and rule hints for new subs come from a quick check by the scraper (no AI).</p>
      {shown.length === 0 ? <p className="empty">Nothing here.</p>
        : <SubTable rows={shown} columns={LIST_COLUMNS} sort={sort} onSort={onSort} selectable={filter === 'unusable' ? null : selectable} />}
    </div>
  );
}

function Competitors({ viewingOrgId }) {
  const [tab, setTab] = useState('profiles');
  return (
    <div className="page">
      <h1>Competitors</h1>
      <p>Get inspired by other Reddit profiles: where, how often and when they post, and the subreddits they use. Times are UTC, like Statistics → Posting times.</p>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid #2a2a2a' }}>
        <button style={tabStyle(tab === 'profiles')} onClick={() => setTab('profiles')}>Profiles</button>
        <button style={tabStyle(tab === 'subs')} onClick={() => setTab('subs')}>Sub list</button>
      </div>
      {/* keyed by org: switching orgs starts the tab afresh */}
      {tab === 'profiles' ? <ProfilesTab key={viewingOrgId || 'own'} orgId={viewingOrgId} /> : <SubListTab key={viewingOrgId || 'own'} orgId={viewingOrgId} />}
    </div>
  );
}

export default Competitors;
